'use client';

import React from 'react';
import { usePathname } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { PAGE_QUERIES } from '@/infrastructure/page/utils/queries';

type JSONLDScriptProps = {
  jsonLd?: Record<string, unknown>;
  initialPathname?: string;
};

const useJSONLDData = (initialJsonLd?: Record<string, unknown>, initialPathname?: string) => {
  const pathname = usePathname();

  const isInitialPage = pathname === initialPathname;
  const shouldFetchNewData = !isInitialPage;

  const { data: pageMetaData } = useQuery({
    ...PAGE_QUERIES.getPageMeta(pathname),
    enabled: shouldFetchNewData,
  });

  if (isInitialPage) {
    return initialJsonLd;
  }

  return pageMetaData?.seoSchema;
};

const JSONLDScript = ({ jsonLd: initialJsonLd, initialPathname }: JSONLDScriptProps) => {
  const currentJsonLd = useJSONLDData(initialJsonLd, initialPathname);

  if (!currentJsonLd) {
    return null;
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(currentJsonLd).replace(/</g, '\\u003c'),
      }}
    />
  );
};

export default JSONLDScript;
